import { FC, useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    LinearProgress,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Alert,
    FormControl,
    InputLabel,
    Select,
    Card,
    CardContent,
    OutlinedInput
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Mail as MailIcon,
    Person as PersonIcon,
    Apps as AppsIcon,
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    AccessTime as AccessTimeIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { adminApiClient } from '../utils/adminApiClient';
import { useSnackbar } from 'notistack';
import { 
    IInvitation, 
    ICreateInvitationAdminDto, 
    IAdminInvitationQuery,
    InvitationStatus 
} from '@tintoa/identity-service-client';

interface InvitationFormData {
    email: string;
    firstname: string;
    lastname: string;
    clientId: string;
    comment?: string;
    roles: string[];
    scopes: string[];
}

interface Client {
    id: string;
    name: string;
    description: string;
}

interface Role {
    id: string;
    name: string;
    ident: string;
}

const statusColors = {
    [InvitationStatus.Pending]: 'warning',
    [InvitationStatus.Accepted]: 'success',
    [InvitationStatus.Declined]: 'error',
    [InvitationStatus.Expired]: 'error',
    [InvitationStatus.Mail_Error]: 'error'
} as const;

const statusIcons = {
    [InvitationStatus.Pending]: <AccessTimeIcon />,
    [InvitationStatus.Accepted]: <CheckCircleIcon />,
    [InvitationStatus.Declined]: <CancelIcon />,
    [InvitationStatus.Expired]: <CancelIcon />,
    [InvitationStatus.Mail_Error]: <ErrorIcon />
} as const;

export const InvitationManagement: FC = () => {
    const [invitations, setInvitations] = useState<IInvitation[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedInvitation, setSelectedInvitation] = useState<IInvitation | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [formData, setFormData] = useState<InvitationFormData>({
        email: '',
        firstname: '',
        lastname: '',
        clientId: '',
        comment: '',
        roles: [],
        scopes: []
    });
    const [filters, setFilters] = useState<IAdminInvitationQuery>({
        limit: 50,
        offset: 0
    });

    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        loadData();
    }, [filters]);

    const loadAvailableRoles = async (clientId?: string) => {
        try {
            if (!clientId) {
                setAvailableRoles([]);
                return;
            }
            const roles = await adminApiClient.getClientRoles(clientId);
            setAvailableRoles(roles as Role[]);
        } catch (error) {
            console.error('Error loading roles:', error);
            enqueueSnackbar('Fehler beim Laden der Rollen', { variant: 'error' });
        }
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const [invitationsResponse, clientsResponse] = await Promise.all([
                adminApiClient.getInvitations(filters),
                adminApiClient.getClients()
            ]);
            
            setInvitations(invitationsResponse.data);
            setClients(clientsResponse);
        } catch (error) {
            console.error('Error loading data:', error);
            enqueueSnackbar('Fehler beim Laden der Daten', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateInvitation = async () => {
        try {
            // creatorTid wird im Backend anhand der Admin-Session gesetzt
            const invitationData = {
                ...formData
            } as ICreateInvitationAdminDto;

            await adminApiClient.createInvitation(invitationData);
            enqueueSnackbar('Einladung erfolgreich erstellt', { variant: 'success' });
            setCreateDialogOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            console.error('Error creating invitation:', error);
            enqueueSnackbar('Fehler beim Erstellen der Einladung', { variant: 'error' });
        }
    };

    const handleDeleteInvitation = async () => {
        if (!selectedInvitation) {
            return;
        }

        try {
            await adminApiClient.deleteInvitation(selectedInvitation.id);
            
            enqueueSnackbar('Einladung erfolgreich gelöscht', { variant: 'success' });
            setDeleteDialogOpen(false);
            setSelectedInvitation(null);
            loadData();
        } catch (error) {
            console.error('Error deleting invitation:', error);
            enqueueSnackbar('Fehler beim Löschen der Einladung', { variant: 'error' });
        }
    };

    const resetForm = () => {
        setFormData({
            email: '',
            firstname: '',
            lastname: '',
            clientId: '',
            comment: '',
            roles: [],
            scopes: []
        });
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, invitation: IInvitation) => {
        setAnchorEl(event.currentTarget);
        setSelectedInvitation(invitation);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        // Don't clear selectedInvitation here, it's needed for the dialog
    };

    const formatDate = (date: Date | string) => {
        return new Date(date).toLocaleDateString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getClientName = (clientId: any) => {
        const clientIdStr = typeof clientId === 'string' ? clientId : clientId.toString();
        const client = clients.find(c => c.id === clientIdStr);
        return client?.name || 'Unbekannt';
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Einladungsverwaltung
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadData}
                        disabled={loading}
                    >
                        Aktualisieren
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                    >
                        Neue Einladung
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Filter
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.status || ''}
                                    label="Status"
                                    onChange={(e) => setFilters({ ...filters, status: e.target.value as InvitationStatus })}
                                >
                                    <MenuItem value="">Alle</MenuItem>
                                    <MenuItem value={InvitationStatus.Pending}>Ausstehend</MenuItem>
                                    <MenuItem value={InvitationStatus.Accepted}>Angenommen</MenuItem>
                                    <MenuItem value={InvitationStatus.Declined}>Abgelehnt</MenuItem>
                                    <MenuItem value={InvitationStatus.Expired}>Abgelaufen</MenuItem>
                                    <MenuItem value={InvitationStatus.Mail_Error}>E-Mail Fehler</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <FormControl fullWidth>
                                <InputLabel>Client</InputLabel>
                                <Select
                                    value={filters.clientId || ''}
                                    label="Client"
                                    onChange={(e) => setFilters({ ...filters, clientId: e.target.value })}
                                >
                                    <MenuItem value="">Alle Clients</MenuItem>
                                    {clients.map((client) => (
                                        <MenuItem key={client.id} value={client.id}>
                                            {client.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <TextField
                                fullWidth
                                label="E-Mail"
                                value={filters.email || ''}
                                onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Invitations Table */}
            <Paper variant="subtle">
                {loading && <LinearProgress />}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>E-Mail</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Client</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Erstellt</TableCell>
                                <TableCell>Läuft ab</TableCell>
                                <TableCell>Rollen</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invitations.map((invitation) => (
                                <TableRow key={invitation.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <MailIcon color="action" />
                                            {invitation.email}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon color="action" />
                                            {invitation.firstname} {invitation.lastname}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AppsIcon color="action" />
                                            {getClientName(invitation.clientId)}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={statusIcons[invitation.status]}
                                            label={invitation.status}
                                            color={statusColors[invitation.status]}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ScheduleIcon color="action" />
                                            {formatDate(invitation.createdAt)}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(invitation.expiresAt)}
                                    </TableCell>
                                    <TableCell>
                                        {invitation.roles && invitation.roles.length > 0 ? (
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {invitation.roles.map((role: string, index: number) => (
                                                    <Chip
                                                        key={index}
                                                        label={role}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Keine
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            onClick={(e) => handleMenuClick(e, invitation)}
                                            size="small"
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Create Invitation Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Neue Einladung erstellen</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="E-Mail"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                            />
                            <FormControl fullWidth required>
                                <InputLabel>Client</InputLabel>
                                <Select
                                    value={formData.clientId}
                                    label="Client"
                                    onChange={(e) => {
                                        const clientId = e.target.value as string;
                                        setFormData({
                                            ...formData,
                                            clientId,
                                            roles: []
                                        });
                                        loadAvailableRoles(clientId);
                                    }}
                                >
                                    {clients.map((client) => (
                                        <MenuItem key={client.id} value={client.id}>
                                            {client.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Vorname"
                                value={formData.firstname}
                                onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                                required
                            />
                            <TextField
                                fullWidth
                                label="Nachname"
                                value={formData.lastname}
                                onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                                required
                            />
                        </Box>
                        <TextField
                            fullWidth
                            label="Kommentar"
                            multiline
                            rows={3}
                            value={formData.comment}
                            onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel id="invitation-roles-label">Rollen (optional)</InputLabel>
                                <Select
                                    labelId="invitation-roles-label"
                                    multiple
                                    value={formData.roles}
                                    input={<OutlinedInput label="Rollen (optional)" />}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            roles: e.target.value as string[]
                                        })
                                    }
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as string[]).map((value) => {
                                                const role = availableRoles.find(
                                                    (r) => r.ident === value
                                                );
                                                return (
                                                    <Chip
                                                        key={value}
                                                        label={role?.name || value}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                >
                                    {availableRoles.map((role) => (
                                        <MenuItem key={role.id} value={role.ident}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start'
                                                }}
                                            >
                                                <Typography variant="body2" fontWeight="medium">
                                                    {role.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {role.ident}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="Scopes (optional, kommasepariert)"
                                value={formData.scopes.join(', ')}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        scopes: e.target.value
                                            .split(',')
                                            .map((s) => s.trim())
                                            .filter(Boolean)
                                    })
                                }
                                helperText="Beispiel: read, write, admin"
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>
                        Abbrechen
                    </Button>
                    <Button 
                        onClick={handleCreateInvitation}
                        variant="contained"
                        disabled={!formData.email || !formData.firstname || !formData.lastname || !formData.clientId}
                    >
                        Erstellen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => {
                setDeleteDialogOpen(false);
                setSelectedInvitation(null);
            }}>
                <DialogTitle>Einladung löschen</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Sind Sie sicher, dass Sie diese Einladung löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
                    </Alert>
                    {selectedInvitation && (
                        <Typography>
                            Einladung für: {selectedInvitation.firstname} {selectedInvitation.lastname} ({selectedInvitation.email})
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setDeleteDialogOpen(false);
                        setSelectedInvitation(null);
                    }}>
                        Abbrechen
                    </Button>
                    <Button 
                        onClick={handleDeleteInvitation}
                        color="error" 
                        variant="contained"
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem 
                    onClick={() => {
                        setDeleteDialogOpen(true);
                        setAnchorEl(null); // Only close the menu, don't clear selectedInvitation
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteIcon color="error" />
                    </ListItemIcon>
                    <ListItemText>Löschen</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    );
};
